import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import { Keyboard, X } from "lucide-react";
import { useMemo } from "react";

const KeyBadge = ({ keys }) => (
  <kbd className="inline-flex items-center justify-center px-2 py-1 bg-gray-200 dark:bg-zinc-700 text-gray-900 dark:text-white rounded border border-gray-300 dark:border-zinc-600 text-xs font-semibold font-mono min-w-[28px]">
    {keys}
  </kbd>
);

export function KeyboardShortcutsDialog({ open, onOpenChange }) {
  // Platform detection
  const isMac = useMemo(
    () =>
      typeof window !== "undefined" &&
      navigator.platform.toUpperCase().indexOf("MAC") >= 0,
    []
  );

  // Keyboard shortcuts organized by category
  const KEYBOARD_SHORTCUTS = useMemo(
    () => [
      {
        category: "Global Shortcuts",
        description: "Available from anywhere in the app",
        shortcuts: [
          {
            action: "New Chat",
            keys: isMac ? ["⌘", "M"] : ["Ctrl", "M"],
            description: "Start a new conversation",
          },
          {
            action: "Search",
            keys: isMac ? ["⌘", "K"] : ["Ctrl", "K"],
            description: "Open search dialog",
          },
          {
            action: "Toggle Sidebar",
            keys: isMac ? ["⌘", "B"] : ["Ctrl", "B"],
            description: "Open or close the sidebar",
          },
        ],
      },
      {
        category: "Message Input",
        description: "When typing in the message input",
        shortcuts: [
          {
            action: "Send Message",
            keys: ["Enter"],
            description: "Send your message",
          },
          {
            action: "New Line",
            keys: ["Shift", "Enter"],
            description: "Add a line break",
          },
          {
            action: "Mention",
            keys: ["@"],
            description: "Mention a file or person",
          },
        ],
      },
      {
        category: "Dialogs & UI",
        description: "When interacting with dialogs and forms",
        shortcuts: [
          {
            action: "Submit Form",
            keys: ["Ctrl", "Enter"],
            description: "Submit the current form",
          },
          {
            action: "Close Dialog",
            keys: ["Escape"],
            description: "Close the active dialog",
          },
        ],
      },
      {
        category: "Navigation",
        description: "Navigate through menus and lists",
        shortcuts: [
          {
            action: "Navigate Menu",
            keys: ["↑", "↓"],
            description: "Move up or down in menus",
          },
          {
            action: "Open Submenu",
            keys: ["Enter"],
            description: "Open a submenu or select an item",
          },
          {
            action: "Close Menu",
            keys: ["Escape"],
            description: "Close the current menu",
          },
        ],
      },
    ],
    [isMac]
  );

  const handleClose = () => {
    onOpenChange(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Escape") {
      e.preventDefault();
      handleClose();
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-[90vw] max-h-[90vh] w-[500px] h-auto bg-white dark:bg-zinc-900 border-gray-200 dark:border-[#3a3a3a] text-gray-900 dark:text-white p-0 gap-0 rounded-2xl flex flex-col overflow-hidden shadow-2xl"
        hideCloseButton={true}
        onKeyDown={handleKeyDown}
      >
        <DialogTitle className="sr-only">Keyboard Shortcuts</DialogTitle>
        <DialogDescription className="sr-only">
          View all available keyboard shortcuts for ArtifactChat
        </DialogDescription>

        {/* Header */}
        <div className="flex items-center justify-between py-3 px-4 bg-white dark:bg-zinc-900 flex-shrink-0 border-b border-gray-100 dark:border-white/5">
          <h2 className="text-lg font-medium text-gray-900 dark:text-white">
            Keyboard Shortcuts
          </h2>
          <button
            onClick={handleClose}
            className="p-1.5 rounded-full hover:bg-gray-100 dark:hover:bg-zinc-800 text-gray-500 dark:text-zinc-400 hover:text-gray-900 dark:hover:text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 flex flex-col overflow-hidden bg-white dark:bg-zinc-900">
          <div className="flex-1 overflow-auto px-4 py-4 space-y-6">
            {KEYBOARD_SHORTCUTS.map((group, groupIndex) => (
              <section key={groupIndex} className="space-y-3">
                <div className="space-y-1">
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                    {group.category}
                  </h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {group.description}
                  </p>
                </div>

                <div className="space-y-2">
                  {group.shortcuts.map((shortcut, shortcutIndex) => (
                    <div
                      key={shortcutIndex}
                      className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-gray-50 dark:hover:bg-zinc-800/50 transition-colors"
                    >
                      <div className="flex-1">
                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                          {shortcut.action}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                          {shortcut.description}
                        </div>
                      </div>
                      <div className="flex items-center gap-1 ml-4">
                        {shortcut.keys.map((key, keyIndex) => (
                          <span key={keyIndex} className="flex items-center">
                            <KeyBadge keys={key} />
                            {keyIndex < shortcut.keys.length - 1 && (
                              <span className="mx-1 text-xs text-gray-400 dark:text-gray-500">
                                +
                              </span>
                            )}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            ))}
          </div>

          {/* Footer */}
          <div className="px-4 py-3 bg-gray-50/50 dark:bg-zinc-900/50 border-t border-gray-100 dark:border-white/5 flex-shrink-0">
            <div className="flex items-start gap-3 rounded-xl bg-gray-50 dark:bg-zinc-800/50 p-3 border border-gray-100 dark:border-white/5">
              <div className="text-blue-500 dark:text-blue-400 mt-0.5">
                <Keyboard className="w-4 h-4" />
              </div>
              <div>
                <strong className="text-gray-700 dark:text-gray-200 block text-xs font-semibold">
                  Tip: Use {isMac ? "⌘" : "Ctrl"}+K to quickly search
                </strong>
                <p className="text-gray-500 dark:text-gray-400 text-[11px] leading-relaxed">
                  Most shortcuts are available from anywhere in the app.
                </p>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default KeyboardShortcutsDialog;
